import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, Spinner } from "react-bootstrap";
import Background from "components/common/background";
import corner4 from "assets/img/illustrations/corner-4.png";
import AdvanceTable from "components/common/advance-table/advanceTable";
import AdvanceTableProvider from "providers/advanceTableProvider";
import ManualPaginationFooter from "components/common/advance-table/manualPaginationFooter";
import SubtleBadge from "components/common/subtleBadge";
import useServerAdvanceTable from "hooks/useServerAdvanceTable";
import { getInitialPageSize } from "../../assets/js/common";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faClock,
  faExclamationTriangle,
  faStopwatch,
  faCalendarAlt,
  faChevronDown
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import moment from "moment";
import i18n from "culture/i18n";

const toHM = (s) => {
  const hUnit = i18n.t("hourShort", "h");
  const mUnit = i18n.t("minuteShort", "m");
  return `${Math.floor(s / 3600)}${hUnit} ${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}${mUnit}`;
};

const mapAttendance = (a) => {
  const clockIn = moment(a.ClockInTime);
  const status = a.RowStatus || "present";

  return {
    date: moment(a.AttendanceDate).format("DD-MM-YYYY"),
    day: i18n.t(`shortDays.${moment(a.AttendanceDate).format("dddd").toLowerCase()}`, moment(a.AttendanceDate).format("ddd")),
    clockIn: clockIn.format("HH:mm"),
    clockOut: a.ClockOutTime ? moment(a.ClockOutTime).format("HH:mm") : null,
    breakTime: toHM(a.breakSeconds ?? a.TotalBreakSeconds ?? 0),
    totalHours: status === "missed_clock_out" ? null : toHM(a.workedSeconds),
    workedSeconds: status === "missed_clock_out" ? 0 : a.workedSeconds,
    status
  };
};

const countWeekdays = (startDate, endDate) => {
  let count = 0;
  for (let d = moment(startDate); d.isSameOrBefore(endDate, "day"); d.add(1, "day")) {
    if (d.isoWeekday() < 6) count += 1;
  }
  return count;
};

const buildSummary = (s, dateFilter, startDate, endDate) => ({
  daysPresent: s.daysPresent || 0,
  workingDays: dateFilter === "all" ? s.daysPresent || 0 : countWeekdays(startDate, endDate),
  lateArrivals: s.lateArrivals || 0,
  missedClockOut: s.missedClockOut || 0,
  totalHours: toHM(s.totalSeconds || 0),
  avgHoursPerDay: s.finishedCount
    ? toHM(Math.round(s.totalSeconds / s.finishedCount))
    : null
});

const MyTimesheet = () => {
  const { t } = useTranslation();
  const [timesheetData, setTimesheetData] = useState([]);
  const [summaryMetrics, setSummaryMetrics] = useState({
    daysPresent: 0,
    workingDays: 22,
    lateArrivals: 0,
    missedClockOut: 0,
    totalHours: toHM(0)
  });
  const [dateFilter, setDateFilter] = useState("all");
  const [activeKpi, setActiveKpi] = useState("all");
  const [dateOpen, setDateOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [pageIndex, setPageIndex] = useState(0); // 0-based, API gets pageIndex + 1
  const [pageSize, setPageSize] = useState(getInitialPageSize("myTimesheetTable"));
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const dateRef = useRef(null);
  const requestRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dateRef.current && !dateRef.current.contains(e.target)) {
        setDateOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const getDateRange = (filterOption) => {
    switch (filterOption) {
      case "currentWeek":
        return {
          startDate: moment().startOf("isoWeek"),
          endDate: moment().endOf("isoWeek")
        };
      case "currentMonth":
        return {
          startDate: moment().startOf("month"),
          endDate: moment().endOf("month")
        };
      case "all":
      default:
        return {
          startDate: moment(0),
          endDate: moment().add(100, "years")
        };
    }
  };

  const statusBadgeConfig = {
    in_progress: { bg: "primary", label: t("inProgress", "In Progress") },
    late: { bg: "warning", label: t("attendanceLate", "Late") },
    weekly_off: { bg: "secondary", label: t("weeklyOff", "Weekly Off") },
    present: { bg: "success", label: t("present", "Present") },
    missed_clock_out: { bg: "danger", label: t("missedClockOut", "Missed Clock Out") },
    on_leave: { bg: "info", label: t("onLeave", "On Leave") }
  };

  const kpiFilterLabels = {
    present: t("daysPresent", "Days Present"),
    late: t("lateArrivals", "Late Arrivals"),
    missed: t("missedClockOut", "Missed Clock Out"),
    hours: t("totalHours", "Total Hours")
  };

  const fetchTimesheet = async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    setError(false);
    try {
      const userId = localStorage.getItem("UserId");
      if (!userId) return;

      const { startDate, endDate } = getDateRange(dateFilter);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/attendanceRoute/myAttendancePaginated`,
        {
          params: {
            userId,
            startDate: startDate.format("YYYY-MM-DD"),
            endDate: endDate.format("YYYY-MM-DD"),
            kpi: activeKpi === "all" ? undefined : activeKpi,
            tzOffset: moment().utcOffset(),
            page: pageIndex + 1,
            pageSize
          },
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          },
          signal: controller.signal
        }
      );

      const data = response.data || {};
      const records = (data.attendances || []).map(mapAttendance);
      setTimesheetData(records);
      setTotalCount(data.totalCount || 0);
      setTotalPages(data.totalPages || 0);

      if (data.totalPages > 0 && pageIndex >= data.totalPages) {
        setPageIndex(data.totalPages - 1);
      }

      setSummaryMetrics(buildSummary(data.summary || {}, dateFilter, startDate, endDate));
    } catch (err) {
      if (axios.isCancel(err)) return;
      console.error("Error fetching timesheet data:", err);
      setError(true);
      setTimesheetData([]);
    } finally {
      if (requestRef.current === controller) setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimesheet();
    return () => requestRef.current?.abort();
  }, [dateFilter, activeKpi, pageIndex, pageSize]);

  const kpiCards = [
    [
      "present",
      t("daysPresent", "Days Present"),
      "success",
      "success",
      faCheck,
      `${t("view", "View")} →`,
      `${t("of", "of")} ${summaryMetrics.workingDays || 22} ${t("workingDays", "working days")}`,
      summaryMetrics.daysPresent || 0
    ],
    [
      "late",
      t("lateArrivals", "Late Arrivals"),
      "warning",
      "warning",
      faClock,
      `${t("view", "View")} →`,
      t("afterShiftStart", "After shift start"),
      summaryMetrics.lateArrivals || 0
    ],
    [
      "missed",
      t("missedClockOut", "Missed Clock Out"),
      "danger",
      "danger",
      faExclamationTriangle,
      `${t("view", "View")} →`,
      t("needsCorrection", "Needs correction"),
      summaryMetrics.missedClockOut || 0
    ],
    [
      "hours",
      t("totalHours", "Total Hours"),
      "primary",
      "primary",
      faStopwatch,
      `${t("view", "View")} →`,
      summaryMetrics.avgHoursPerDay
        ? `${t("avgPerDay", "Avg.")} ${summaryMetrics.avgHoursPerDay} ${t("perDay", "per day")}`
        : t("totalWorkingHours", "Total working hours"),
      summaryMetrics.totalHours || toHM(0)
    ]
  ];

  const columns = [
    {
      accessorKey: "date",
      header: t("date", "Date"),
      meta: { headerProps: { className: "text-900" } },
      cell: ({ row: { original } }) => (
        <span className="text-700">{original.date}</span>
      )
    },
    {
      accessorKey: "day",
      header: t("day", "Day"),
      meta: { headerProps: { className: "text-900 text-capitalize" } },
      cell: ({ row: { original } }) => (
        <span className="text-700">{original.day}</span>
      )
    },
    {
      accessorKey: "clockIn",
      header: t("clockIn", "Clock In"),
      meta: { headerProps: { className: "text-900" } },
      cell: ({ row: { original } }) => (
        <span className="text-700">{original.clockIn || "—"}</span>
      )
    },
    {
      accessorKey: "clockOut",
      header: t("clockOut", "Clock Out"),
      meta: { headerProps: { className: "text-900" } },
      cell: ({ row: { original } }) => (
        <span className="text-700">{original.clockOut || "—"}</span>
      )
    },
    {
      accessorKey: "breakTime",
      header: t("break", "Break"),
      meta: { headerProps: { className: "text-900" } },
      cell: ({ row: { original } }) => (
        <span className="text-700">{original.breakTime || toHM(0)}</span>
      )
    },
    {
      accessorKey: "totalHours",
      header: t("totalHours", "Total Hours"),
      meta: { headerProps: { className: "text-900" } },
      cell: ({ row: { original } }) => (
        <span className="text-700">{original.totalHours || "—"}</span>
      )
    },
    {
      accessorKey: "status",
      header: t("status", "Status"),
      meta: { headerProps: { className: "text-900" } },
      cell: ({ row: { original } }) => {
        const config = statusBadgeConfig[original.status] || {
          bg: "secondary",
          label: original.status || "—"
        };
        return (
          <SubtleBadge pill bg={config.bg}>
            <span className="px-1">{config.label}</span>
          </SubtleBadge>
        );
      }
    }
  ];

  const table = useServerAdvanceTable({
    data: timesheetData,
    columns,
    selection: false,
    sortable: false,
    pagination: true,
    manualPagination: true,
    pageCount: totalPages,
    paginationState: { pageIndex, pageSize },
    onPaginationChange: (updater) => {
      const next = typeof updater === "function" ? updater({ pageIndex, pageSize }) : updater;
      setPageIndex(next.pageIndex);
      setPageSize(next.pageSize);
    }
  });

  const renderTableBody = () => {
    if (loading) {
      return (
        <div className="d-flex justify-content-center align-items-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t("loading", "Loading...")}</span>
          </Spinner>
        </div>
      );
    }
    if (error) {
      return (
        <div className="d-flex justify-content-center align-items-center py-5 text-danger">
          {t("somethingWentWrong", "Something went wrong. Please try again.")}
        </div>
      );
    }
    if (timesheetData.length === 0) {
      return (
        <div className="d-flex justify-content-center align-items-center py-5 text-muted">
          {t("noDataAvailable", "No data available")}
        </div>
      );
    }
    return (
      <AdvanceTable
        headerClassName="bg-200 text-900 text-nowrap align-middle"
        rowClassName="align-middle white-space-nowrap"
        tableProps={{
          bordered: false,
          hover: true,
          size: "sm",
          className: "mb-0 overflow-hidden fs-10"
        }}
      />
    );
  };

  return (
    <>
      <Row className="g-3 mb-3">
        <Col xs={12}>
          <Card className="mb-3 h-100 overflow-hidden">
            <Background image={corner4} className="bg-card" />
            <Card.Body className="position-relative pe-3 pb-2">
              <Row className="flex-between-center">
                <Col md>
                  <h5 className="mb-2 mb-md-0">{t("myTimesheet", "My Timesheet")}</h5>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        {kpiCards.map(([key, label, color, badgeColor, icon, actionLabel, subtext, val]) => (
          <Col xs={6} sm={6} xl={3} key={key}>
            <Card
              className={`absence-kpi-card absence-kpi-border-${color} h-100 flex-grow-0 rounded-3 card-hover border cursor-pointer ${
                activeKpi === key
                  ? `absence-kpi-active absence-kpi-selected-${color}`
                  : "shadow-none"
              }`}
              role="button"
              tabIndex={0}
              onClick={() => {
                setActiveKpi((prev) => (prev === key ? "all" : key));
                setPageIndex(0);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setActiveKpi((prev) => (prev === key ? "all" : key));
                  setPageIndex(0);
                }
              }}
            >
              <Card.Body className="py-3 px-3">
                <div className="mb-2">
                  <div
                    className={`d-flex align-items-center justify-content-center rounded-2 stat-icon bg-${badgeColor}-subtle`}
                  >
                    <FontAwesomeIcon
                      icon={icon}
                      className={`fs-8 text-${color}`}
                    />
                  </div>
                </div>

                <div className="mb-1 fw-bolder font-size-22">{val}</div>

                <span className="mb-1 fw-semibold text-900 font-size-13 d-block">
                  {label}
                </span>

                <div className="d-flex justify-content-between align-items-center gap-2">
                  <div className="font-size-12 text-600 fw-normal">
                    {subtext}
                  </div>
                  <div
                    className={`p-0 d-inline-block text-decoration-none fw-medium font-size-12 text-${color} text-nowrap`}
                  >
                    {actionLabel}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <AdvanceTableProvider {...table}>
        <Card className="mb-3">
          <Card.Header className="pb-2">
            <Row className="align-items-center">
              <Col xs={12} className="d-flex justify-content-end align-items-center gap-2">
                {activeKpi !== "all" && (
                  <span className="badge rounded-pill fw-medium font-size-11 badge-subtle-primary d-flex align-items-center gap-1 px-2 py-1">
                    {`${t("filtered", "Filtered")}: ${kpiFilterLabels[activeKpi]}`}
                    <button
                      type="button"
                      className="btn-close p-0"
                      aria-label={t("clear", "Clear")}
                      onClick={() => {
                        setActiveKpi("all");
                        setPageIndex(0);
                      }}
                    />
                  </span>
                )}

                <div
                  className="d-flex align-items-center input-group-sm filter-group border rounded position-relative w-auto"
                  ref={dateRef}
                >
                  <span className="px-2 bg-light border-end">
                    <FontAwesomeIcon icon={faCalendarAlt} className="text-600" />
                  </span>
                  <button
                    type="button"
                    className="btn border-0 bg-transparent d-flex align-items-center justify-content-between px-2 text-900 fs--1 fw-normal filter-dropdown-btn"
                    onClick={() => setDateOpen((prev) => !prev)}
                  >
                    {dateFilter === "all" ? t("allLabel", "All") : t(dateFilter)}
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      size="xs"
                      className="ms-2 me-1 text-800 fw-normal"
                    />
                  </button>
                  {dateOpen && (
                    <div className="dropdown-menu dropdown-menu-falcon show shadow-sm py-1 filter-dropdown-menu">
                      {[
                        ["all", t("allLabel", "All")],
                        ["currentWeek", t("currentWeek", "Current Week")],
                        ["currentMonth", t("currentMonth", "Current Month")]
                      ].map(([val, label]) => (
                        <button
                          key={val}
                          type="button"
                          className="dropdown-item animation-none fs--1 filter-dropdown-item"
                          onClick={() => {
                            setDateFilter(val);
                            setPageIndex(0);
                            setDateOpen(false);
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Header>

          <Card.Body className="p-0">
            {renderTableBody()}
          </Card.Body>

          <Card.Footer>
            <ManualPaginationFooter
              tableKey="myTimesheetTable"
              totalCount={totalCount}
              pageIndex={pageIndex}
              pageSize={pageSize}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPageIndex(0);
              }}
            />
          </Card.Footer>
        </Card>
      </AdvanceTableProvider>
    </>
  );
};

export default MyTimesheet;